import React, { Component } from "react";
import { connect } from "react-redux";
import { withStyles } from "@mui/styles";
import axios from 'axios';
// claude: react-markdown v8 (last major that supports React 17). Renders the
// server's response text as CommonMark. Server side, JInformServlet.println
// emits \n\n so single println() calls become paragraph breaks — required for
// markdown to see them as such.
import ReactMarkdown from 'react-markdown';
import { hostname } from '../host';

// claude: titleId (/bookshelf/:titleId) -> display name for the terminal's
// header band. Kept in sync with Home.js TITLES by route: default->Volatile,
// story1->Please Enjoy Your Stay, story2->Dead Inside. Unknown ids fall back to
// the house label (the game's own banner still prints in the transcript below).
const TITLE_NAMES = {
  default: "Volatile",
  story1: "Please Enjoy Your Stay",
  story2: "Dead Inside",
};

// claude: the terminal drops the old garish "blue-screen" (#1f5ea8/#fff) for the
// portal's house DARK palette (see theme/portal-chrome.css :root tokens, loaded
// globally via App.js). Body + prompt stay a TRUE monospace (--nf-font-mono, IBM
// Plex Mono) so the engine's ASCII-box `pre` entries keep their alignment — the
// hard fixed-width requirement. The typewriter face --nf-font-type (Special Elite)
// is proportional, so it's reserved for the header band + paywall primary, where
// nothing needs to align.
const styles = () => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)", // adjust if you have an app bar
    boxSizing: "border-box",
    padding: "16px 16px 80px",   // 80 bottom matches footer; 16 frames the panel
    background: "var(--nf-ground)",
    color: "var(--nf-ink)",
    fontFamily: "var(--nf-font-mono)",
  },
  // claude: the terminal is now a contained, bordered panel floating on the
  // ground rather than a full-bleed wash — a calmer fit with the rest of the site.
  panel: {
    flex: 1,
    minHeight: 0,            // let the inner screen scroll inside a flex child
    width: "100%",
    maxWidth: "92ch",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    background: "var(--nf-surface)",
    border: "1px solid var(--nf-hairline)",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(0,0,0,0.45)",
  },
  // claude: title band in the typewriter face (Special Elite). Proportional, but
  // it's a single free-flowing line so alignment doesn't matter here.
  header: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    padding: "10px 16px",
    borderBottom: "1px solid var(--nf-hairline)",
  },
  headerTitle: {
    fontFamily: "var(--nf-font-type)",
    fontSize: "1.05rem",
    letterSpacing: ".01em",
    color: "var(--nf-ink)",
  },
  headerKicker: {
    marginLeft: "auto",
    fontFamily: "var(--nf-font-mono)",
    fontSize: 10,
    letterSpacing: ".16em",
    textTransform: "uppercase",
    color: "var(--nf-muted)",
  },
  // claude: the [DEV] affordance, shown only for privileged accounts (server's
  // X-Bookshelf-Developer header). Mono/uppercase to sit in the terminal chrome.
  devChip: {
    fontFamily: "var(--nf-font-mono)",
    fontSize: 10,
    letterSpacing: ".16em",
    textTransform: "uppercase",
    color: "var(--nf-muted)",
    background: "transparent",
    border: "1px solid var(--nf-hairline)",
    borderRadius: 3,
    padding: "2px 6px",
    cursor: "pointer",
    "&:hover": { color: "var(--nf-ink)", borderColor: "var(--nf-ink)" },
  },
  screen: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: 16,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  entry: {
    marginBottom: 10,
  },
  // claude: scoped styles for markdown-rendered text entries. react-markdown
  // emits standard HTML elements (<p>, <h1..h6>, <ul>, <ol>, <li>, <code>,
  // <pre>, <blockquote>, <a>, <hr>, <img>). Defaults would be a bit too
  // browser-y for a terminal aesthetic; these tighten the spacing and
  // recolor code/links/quotes to sit on the dark surface legibly.
  markdownEntry: {
    marginBottom: 10,
    "& p": {
      margin: "0 0 0.6em 0",
      "&:last-child": { marginBottom: 0 },
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      margin: "0.6em 0 0.3em 0",
      fontWeight: "bold",
    },
    "& ul, & ol": {
      margin: "0.4em 0",
      paddingLeft: "2em",
    },
    "& li": {
      marginBottom: "0.2em",
    },
    "& code": {
      background: "rgba(236,228,215,0.08)",
      padding: "0 4px",
      borderRadius: 3,
    },
    "& pre": {
      background: "var(--nf-ground)",
      padding: "8px 12px",
      borderRadius: 4,
      overflowX: "auto",
      margin: "0.5em 0",
      "& code": {
        background: "transparent",
        padding: 0,
      },
    },
    "& blockquote": {
      borderLeft: "3px solid var(--nf-hairline)",
      margin: "0.5em 0",
      padding: "0 0 0 1em",
      color: "var(--nf-muted)",
    },
    "& a": {
      color: "var(--nf-accent)",
      textDecoration: "underline",
    },
    "& hr": {
      border: 0,
      borderTop: "1px solid var(--nf-hairline)",
      margin: "0.8em 0",
    },
    "& img": {
      maxWidth: "100%",
    },
  },
  pre: {
    margin: 0,
    whiteSpace: "pre",
  },
  promptLine: {
    marginTop: 6,
    whiteSpace: "pre-wrap",
  },
  caret: {
    display: "inline-block",
    width: 8,
    marginLeft: 2,
    // simple “block cursor” feel — the rust accent reads as the live cursor
    background: "var(--nf-accent)",
    height: "1.1em",
    transform: "translateY(2px)",
  },
  // claude: the live "> " marker, tinted with the house accent so the input line
  // is visually distinct from the ink-colored transcript.
  promptMark: {
    color: "var(--nf-accent)",
  },
  // claude: visually hidden but still focusable. Kept in normal flow (NOT
  // pinned to the viewport bottom) so that focusing it doesn't make the mobile
  // browser scroll the transcript — and the site header — off the top when the
  // soft keyboard opens. See updateViewportHeight for the companion fix that
  // shrinks the root to the visible viewport.
  hiddenInput: {
    opacity: 0,
    width: 1,
    height: 1,
    border: 0,
    padding: 0,
    margin: 0,
    pointerEvents: "none",
  },
  // claude: pulsing "..." shown between the transcript and the persistent
  // "> " prompt while a request to /rest/bookshelf is in flight. Prevents the
  // slow-LLM UX where the empty prompt below the echoed command reads as an
  // empty server response.
  waitingLine: {
    marginTop: 6,
    marginBottom: 10,
    opacity: 0.6,
    animation: "$jinformWaitingPulse 1.4s ease-in-out infinite",
  },
  "@keyframes jinformWaitingPulse": {
    "0%, 100%": { opacity: 0.25 },
    "50%":      { opacity: 0.85 },
  },
  // claude: the paywall action row shown in place of the "> " prompt once walled.
  // The wall TEXT is already in the transcript (the server's emitPaywallNotice
  // body); these are the exits — Purchase (placeholder until Stage D) and Restart.
  wallPanel: {
    marginTop: 14,
    marginBottom: 10,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  wallButton: {
    fontFamily: "var(--nf-font-mono)",
    fontSize: "1em",
    color: "var(--nf-ink)",
    background: "transparent",
    border: "1px solid var(--nf-hairline)",
    padding: "8px 18px",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": { borderColor: "var(--nf-muted)", background: "rgba(236,228,215,0.06)" },
  },
  wallButtonPrimary: {
    // claude: the one place Special Elite earns a spot in the action row — a
    // short standalone label, no alignment at stake.
    fontFamily: "var(--nf-font-type)",
    fontSize: "1em",
    color: "#fff",
    background: "var(--nf-accent)",
    border: "1px solid var(--nf-accent)",
    padding: "8px 18px",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": { background: "var(--nf-accent-hover)", borderColor: "var(--nf-accent-hover)" },
  },
});

class JInform extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // transcript entries: { type: 'text'|'pre', text: string }
      entries: [
        /*{
          type: "text",
          text:
            "You swipe your keycard, wait for the familiar green light, and shoulder open the door.\n\nThe door clicks shut behind you.",
        },
        {
          type: "pre",
          text:
            "+---------------------+\n" +
            "|  PLEASE ENJOY       |\n" +
            "|  YOUR STAY          |\n" +
            "+---------------------+",
        },*/
      ],
      currentInput: "",
      history: [],
      historyIndex: -1, // -1 means “not browsing history”
      isComposing: false, // for IME
      // claude: true once a response carries X-Bookshelf-Paywall — the playthrough
      // is past the free-demo boundary and unowned. Swaps the input prompt for the
      // wall's Purchase / Restart-demo actions.
      walled: false,
      // claude: true once a load response carries X-Bookshelf-Developer — the account
      // may see developer options. The server gates this on the privileged owner/dev
      // ("admin") role, separate from deluxe content (a paying deluxe customer is NOT a
      // developer). UI hint only — every dev action is still authorized server-side.
      // Gates the [DEV] affordance in the header.
      isDeveloper: false,
      // claude: true while a request to /rest/bookshelf is in flight. Renders a
      // pulsing "..." between the transcript and the "> " prompt so a slow LLM
      // response doesn't look like an empty server reply. Starts true because
      // the initial POST fires from the constructor.
      isWaiting: true,
      // claude: height (px) of the currently-visible viewport, tracked via the
      // visualViewport API. null until measured in componentDidMount, at which
      // point the root's height is driven from this so the container shrinks to
      // the visible area when the mobile keyboard opens (keeping the prompt in
      // view instead of scrolling it off the top). See updateViewportHeight.
      viewportHeight: null,
    };

    this.screenRef = React.createRef();
    this.hiddenInputRef = React.createRef();

    // claude: launch the title named in the route (/bookshelf/:titleId) —
    // e.g. "story1" -> Jasper, "default" -> Volatile — rather than hardcoding
    // one game. Stored on the instance so submitCommand/loadTitle reuse it.
    this.titleId = this.props.match.params.titleId;

    // Kick off the initial load. isWaiting starts true (set in initial state),
    // so we don't setState synchronously here in the constructor.
    this.loadTitle();
  }

  // claude: POST /bookshelf/:titleId to (re)load the title, appending the
  // server's opening text (banner + first room). Reused by the constructor and
  // by reloadTitle after a blue-screen restart/restore. Callers own clearing the
  // transcript and raising isWaiting beforehand; this only lowers isWaiting once
  // the load settles.
  loadTitle = () => {
    let self = this;
    let titleId = this.titleId;
    let playthroughId = "ABC";
    let sessionId = "asdfasdf";

    axios.post(hostname() + 'rest/bookshelf/' + titleId + '?playthroughId=' + playthroughId + '&sessionId=' + sessionId)
			.then(function (response) {
        // claude: X-Bookshelf-Paywall means the load returned the wall (not the
        // room) — a returning player past the free-demo boundary, unowned.
        var paywall = response.headers && response.headers['x-bookshelf-paywall'];
        // claude: X-Bookshelf-Developer marks a privileged (owner/dev "admin" role)
        // account — expose the [DEV] affordance. Distinct from the deluxe-content gate;
        // server-authorized, so a forged flag unlocks nothing.
        var developer = response.headers && response.headers['x-bookshelf-developer'];
        self.appendOutput([{ type: "text", text: response.data }]);
        self.setState({ walled: !!paywall, isDeveloper: !!developer });
			})
			.catch(function (error) {
				console.log(error);
			})
			.then(function () {
				// clear the waiting indicator once the load settles, success or failure.
				self.setState({ isWaiting: false });
			});
  };

  // claude: a blue-screen meta verb (restart/restore) reset/rolled-back the
  // playthrough server-side; the PUT's own body is just "Ok." and never carries
  // the fresh opening. Clear the screen and re-load so the restarted game's
  // banner (or the restored room) is shown — the load path is the only one that
  // emits it. isWaiting stays up until the reload settles.
  reloadTitle = () => {
    // A restart/restore fetches a fresh opening; clear the wall and let the reload
    // recompute it (a restart drops the player back into the free demo).
    this.setState({ entries: [], isWaiting: true, walled: false }, this.loadTitle);
  };

  componentDidMount() {
    // Focus the hidden input on load so typing “just works”
    this.focusInput();
    this.scrollToBottom();

    // claude: track the visible viewport so the root shrinks when the mobile
    // soft keyboard opens. Without this the root stays a full 100vh box and the
    // browser scrolls the header + prompt off the top to reveal the input.
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", this.updateViewportHeight);
      vv.addEventListener("scroll", this.updateViewportHeight);
    }
    this.updateViewportHeight();
  }

  componentWillUnmount() {
    const vv = window.visualViewport;
    if (vv) {
      vv.removeEventListener("resize", this.updateViewportHeight);
      vv.removeEventListener("scroll", this.updateViewportHeight);
    }
  }

  updateViewportHeight = () => {
    const vv = window.visualViewport;
    const h = vv ? vv.height : window.innerHeight;
    // keep the transcript pinned to the bottom as the keyboard animates in/out
    this.setState({ viewportHeight: h }, this.scrollToBottom);
  };

  componentDidUpdate(prevProps, prevState) {
    // claude: also scroll on isWaiting transitions so the "..." indicator
    // (appearing above the prompt) and the response text that replaces it
    // both land in view.
    if (prevState.entries.length !== this.state.entries.length
        || prevState.isWaiting !== this.state.isWaiting) {
      this.scrollToBottom();
    }
  }

  focusInput = () => {
    const el = this.hiddenInputRef.current;
    if (!el) return;

    // Modern browsers: don't scroll the page just to focus
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      // Fallback: older browsers
      el.focus();
    }
  };

  scrollToBottom = () => {
    const el = this.screenRef.current;
    if (!el) return;
    // keep it simple: always scroll to bottom on new output
    el.scrollTop = el.scrollHeight;
  };

  appendOutput = (chunks) => {
    // chunks: array of {type, text}
    this.setState((s) => ({
      entries: [...s.entries, ...chunks],
    }));
  };

  submitCommand = () => {
    const raw = this.state.currentInput;
    const command = raw.trim();

    // claude: while walled, typed input is inert (the server freezes the turn);
    // the wall's buttons are the only way forward.
    if (this.state.walled) {
      return;
    }

    // always echo the prompt line, even if empty (classic IF feel)
    this.appendOutput([{ type: "text", text: `> ${raw}` }]);

    // reset input & history browsing
    this.setState((s) => ({
      currentInput: "",
      history: command ? [...s.history, command] : s.history,
      historyIndex: -1,
    }));

    if (!command) {
      // empty command = do nothing else
      return;
    }

    this.dispatchCommand(command);
  };

  // claude: factored network send shared by typed input (submitCommand) and the
  // paywall "Restart demo" button. Handles the two response signals — X-Bookshelf-
  // Reload (a meta verb reset/rolled-back → clear + reload) and X-Bookshelf-Paywall
  // (the wall → swap the prompt for the Purchase/Restart actions). axios lowercases
  // header names.
  dispatchCommand = (command) => {
    let self = this;
    let titleId = this.titleId;
    let playthroughId = "ABC";
    let sessionId = "asdfasdf";

    this.setState({ isWaiting: true });

		axios.put(hostname() + 'rest/bookshelf/' + titleId + '?playthroughId=' + playthroughId + '&sessionId=' + sessionId + '&input=' + command)
			.then(function (response) {
        var reload = response.headers && response.headers['x-bookshelf-reload'];
        if (reload) {
          // keep the waiting indicator up; reloadTitle lowers it (and clears the
          // wall) when the fresh load settles.
          self.reloadTitle();
          return;
        }
        var paywall = response.headers && response.headers['x-bookshelf-paywall'];
        self.appendOutput([{ type: "text", text: response.data }]);
				self.setState({ isWaiting: false, walled: !!paywall });
			})
			.catch(function (error) {
				console.log(error);
				self.setState({ isWaiting: false });
			});
  };

  // claude: the wall's "Restart demo" — sends the RESTART meta verb, which sails
  // through the paywall freeze server-side, dropping the player back into the free
  // demo from the top.
  restartDemo = () => {
    this.appendOutput([{ type: "text", text: "> restart" }]);
    this.dispatchCommand("restart");
  };

  // claude: the wall's "Purchase to continue" — placeholder until the Shopify
  // checkout (portal Stage D) is wired. Marks where checkout will live.
  handleBuy = () => {
    this.appendOutput([{ type: "text", text: "_Checkout isn't wired up yet — this is where the purchase flow will go._" }]);
  };

  // claude: developer-options entry point, shown only when the server sent
  // X-Bookshelf-Developer (isDeveloper). Placeholder until concrete dev tools are
  // wired — each will still be authorized server-side, so this affordance can't
  // unlock anything on its own. Marks where the dev menu will open.
  handleDeveloperOptions = () => {
    this.appendOutput([{ type: "text", text: "_Developer options aren't wired up yet — this is where they'll go._" }]);
  };

  handleKeyDown = (e) => {
    // If user clicked elsewhere, keep typing “in the console”
    // (We still let normal shortcuts like Ctrl+C work.)
    if (e.key === "Enter") {
      if (this.state.isComposing) return;
      e.preventDefault();
      this.submitCommand();
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      this.browseHistory(-1);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.browseHistory(1);
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      this.setState({ currentInput: "", historyIndex: -1 });
      return;
    }
  };

  browseHistory = (delta) => {
    const { history, historyIndex } = this.state;
    if (!history.length) return;

    // When starting to browse, jump to last entry
    let nextIndex = historyIndex;
    if (nextIndex === -1) nextIndex = history.length;

    nextIndex += delta;
    if (nextIndex < 0) nextIndex = 0;
    if (nextIndex > history.length) nextIndex = history.length;

    if (nextIndex === history.length) {
      // “after the newest” = blank input
      this.setState({ historyIndex: nextIndex, currentInput: "" });
    } else {
      this.setState({ historyIndex: nextIndex, currentInput: history[nextIndex] });
    }
  };

  renderEntry = (entry, idx) => {
    const { classes } = this.props;
    if (entry.type === "pre") {
      // claude: intentionally NOT run through markdown — pre entries are for
      // ASCII art / spatial layouts / tabular text that markdown would mangle.
      return (
        <div className={classes.entry} key={idx}>
          <pre className={classes.pre}>{entry.text}</pre>
        </div>
      );
    }
    // claude: text entries are rendered as CommonMark. Echoed input (which
    // submitCommand adds as `> raw`) passes through too, but since it never
    // contains markdown syntax intentionally it renders as a plain paragraph.
    // If we ever want to prevent user text from being interpreted as markdown
    // (e.g., someone types `**hello**` and expects to see the literal),
    // switch that appendOutput to a new type like 'literal' or escape here.
    return (
      <div className={classes.markdownEntry} key={idx}>
        <ReactMarkdown>{entry.text}</ReactMarkdown>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const { entries, currentInput, isWaiting, walled, isDeveloper, viewportHeight } = this.state;

    // claude: when we have a visualViewport measurement, drive the root height
    // from it (minus the 64px app bar above us) so the container matches the
    // visible area when the keyboard is open. Falls back to the CSS
    // `calc(100vh - 64px)` until the first measurement lands.
    const rootStyle =
      viewportHeight != null ? { height: viewportHeight - 64 } : undefined;

    const titleName = TITLE_NAMES[this.titleId] || "Newfangle Press";

    return (
      <div className={classes.root} style={rootStyle} onClick={this.focusInput}>
        <div className={classes.panel}>
        <div className={classes.header}>
          <span className={classes.headerTitle}>{titleName}</span>
          <span className={classes.headerKicker}>Bookshelf</span>
          {/* claude: developer-options affordance, gated on the server's
              X-Bookshelf-Developer header (isDeveloper). Placeholder for now —
              handleDeveloperOptions just notes where the dev menu will open. Rendered
              only for the privileged owner/dev ("admin") role — NOT deluxe customers;
              a client that force-shows it still can't invoke anything the server won't
              independently authorize. */}
          {isDeveloper && (
            <button
              type="button"
              className={classes.devChip}
              onClick={this.handleDeveloperOptions}
              title="Developer options"
            >
              DEV
            </button>
          )}
        </div>
        <div className={classes.screen} ref={this.screenRef}>
          {entries.map(this.renderEntry)}

          {/* claude: pulsing "..." while a request is in flight. Sits between
              the transcript and the prompt line, so the visual sequence is:
                > look at graham       (echoed command, entered as an entry)
                ...                    (this indicator, pulses while server thinks)
                >                      (persistent prompt, appears only after response)
              Once the response arrives, isWaiting flips false: the indicator
              vanishes, the response text is already in `entries` from
              appendOutput, and the "> " prompt line reappears below it.
              The prompt is suppressed while isWaiting so the user doesn't
              see a "ready for input" cue while the server is still working. */}
          {isWaiting && (
            <div className={classes.waitingLine}>...</div>
          )}

          {/* claude: the paywall wall — the wall TEXT is already in the transcript
              (the server's emitPaywallNotice body); this is the action row that
              replaces the input prompt while walled. */}
          {!isWaiting && walled && (
            <div className={classes.wallPanel}>
              <button className={classes.wallButtonPrimary} onClick={this.handleBuy}>
                Purchase to continue
              </button>
              <button className={classes.wallButton} onClick={this.restartDemo}>
                Restart demo
              </button>
            </div>
          )}

          {/* “Typing directly into the console” line — hidden while a request is
              in flight (competes with "...") or while walled (input is frozen). */}
          {!isWaiting && !walled && (
            <div className={classes.promptLine}>
              <span className={classes.promptMark}>{"> "}</span>
              {currentInput}
              <span className={classes.caret} />
            </div>
          )}
        </div>
        </div>

        {/* The actual input lives here (invisible) */}
        <textarea
          ref={this.hiddenInputRef}
          className={classes.hiddenInput}
          data-1p-ignore="true"
          value={currentInput}
          onChange={(e) => this.setState({ currentInput: e.target.value })}
          onKeyDown={this.handleKeyDown}
          onCompositionStart={() => this.setState({ isComposing: true })}
          onCompositionEnd={() => this.setState({ isComposing: false })}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(JInform));
